import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data-loading", "failed", "showResult", "labelCompletedText", "showLabel", "label", "layout", "minHeight", "grow", "resultAnimationDuration", "onResultDisplayFinish", "resultIcon", "use", "size"];
import { useEffect, useRef } from 'react';
import { Flex } from '../../../components/Flex';
import { Text } from '../../../components/Text';
import { SuccessIcon } from 'foundations-assets';
import styled, { useTheme } from 'styled-components';
import { getIconColor, getIconSize, getOuterFlexProps, getInnerFlexProps, getRingColor, getIndicatorColor } from './LoadingSpinner.helpers';
import { m } from 'framer-motion';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LoadingSpinnerWrapper = styled(m.div).withConfig({
  displayName: "LoadingSpinner__LoadingSpinnerWrapper",
  componentId: "sc-19b20gl-0"
})(["", ""], ({
  $showResult
}) => $showResult && `margin-left: -1px;
    width: 1px;
  `);
const ResultSpinner = styled(m.div).withConfig({
  displayName: "LoadingSpinner__ResultSpinner",
  componentId: "sc-19b20gl-1"
})(["display:flex;align-items:center;justify-content:center;position:", ";"], ({
  $showResult
}) => $showResult ? 'relative' : 'absolute');
const RingPath = styled(m.circle).withConfig({
  displayName: "LoadingSpinner__RingPath",
  componentId: "sc-19b20gl-2"
})(["stroke-linecap:round;transform-origin:center;"]);
const Ring = styled.svg.withConfig({
  displayName: "LoadingSpinner__Ring",
  componentId: "sc-19b20gl-3"
})(["display:block;max-width:100%;"]);
export const LoadingSpinner = _ref => {
  let {
      failed = false,
      showResult = false,
      labelCompletedText = 'Loaded',
      showLabel = false,
      label = 'Loading',
      layout,
      minHeight,
      grow = false,
      resultAnimationDuration = 1500,
      onResultDisplayFinish = () => {},
      resultIcon: ResultIcon = SuccessIcon,
      use = 'link',
      size = 'sm'
      // SpaceStyleProps & DOMProps
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const renderedLabel = showResult && !failed ? labelCompletedText : showLabel ? label : null;
  const resultDisplayTimeoutRef = useRef();
  useEffect(() => {
    if (!showResult) return undefined;
    resultDisplayTimeoutRef.current = setTimeout(onResultDisplayFinish, resultAnimationDuration);
    return () => {
      clearTimeout(resultDisplayTimeoutRef.current);
    };
  }, [onResultDisplayFinish, resultAnimationDuration, showResult]);
  const LoadingSpinnerWrapperVariants = {
    initial: {
      opacity: 1,
      scale: 1,
      rotate: 0
    },
    final: {
      opacity: 0,
      scale: 0.7,
      rotate: 360
    }
  };
  const ResultSpinnerVariants = {
    initial: {
      opacity: 0,
      scale: 0.7
    },
    final: {
      opacity: 1,
      scale: 1
    }
  };
  return /*#__PURE__*/_jsxs(Flex, Object.assign({}, getOuterFlexProps(grow, layout, minHeight, renderedLabel), rest, {
    children: [/*#__PURE__*/_jsxs(Flex, Object.assign({}, getInnerFlexProps(size), {
      children: [/*#__PURE__*/_jsx(LoadingSpinnerWrapper, {
        $showResult: showResult,
        initial: LoadingSpinnerWrapperVariants['initial'],
        animate: showResult ? LoadingSpinnerWrapperVariants['final'] : LoadingSpinnerWrapperVariants['initial'],
        transition: {
          opacity: {
            duration: 0.2,
            ease: [0.42, 0, 0.58, 1]
          },
          scale: {
            duration: 0.2,
            ease: [0.89, 0.03, 0.68, 0.22]
          },
          rotate: {
            duration: 2,
            repeat: Infinity,
            ease: 'linear'
          }
        },
        children: /*#__PURE__*/_jsxs(Ring, {
          height: theme.size[getIconSize(size)],
          width: theme.size[getIconSize(size)],
          viewBox: "0 0 50 50",
          stroke: getRingColor(theme, use),
          children: [/*#__PURE__*/_jsx("circle", {
            cx: "25",
            cy: "25",
            r: "22.5",
            fill: "none",
            strokeWidth: "5"
          }), /*#__PURE__*/_jsx(RingPath, {
            cx: "25",
            cy: "25",
            r: "22.5",
            fill: "none",
            stroke: theme.color[getIndicatorColor(use)],
            strokeWidth: "5",
            initial: {
              strokeDasharray: '1, 150',
              strokeDashoffset: 0,
              rotate: 0
            },
            animate: {
              strokeDasharray: ['1, 150', '90, 150', '90, 150'],
              strokeDashoffset: [0, -50, -140],
              rotate: [0, 360]
            },
            transition: {
              strokeDasharray: {
                duration: 2,
                repeat: Infinity,
                ease: [0.42, 0, 0.58, 1]
              },
              strokeDashoffset: {
                duration: 2,
                repeat: Infinity,
                ease: 'linear'
              },
              rotate: {
                duration: 2,
                repeat: Infinity,
                ease: 'linear'
              }
            }
          })]
        })
      }), ResultIcon && !failed && /*#__PURE__*/_jsx(ResultSpinner, {
        $showResult: showResult,
        initial: ResultSpinnerVariants['initial'],
        animate: showResult ? ResultSpinnerVariants['final'] : ResultSpinnerVariants['initial'],
        transition: {
          opacity: {
            duration: 0.2,
            ease: [0.42, 0, 0.58, 1]
          },
          scale: {
            duration: 0.3,
            ease: [0.2, 0.9, 0.3, 2]
          }
        },
        children: /*#__PURE__*/_jsx(ResultIcon, {
          color: getIconColor(use),
          size: getIconSize(size)
        })
      })]
    })), /*#__PURE__*/_jsx(Text, Object.assign({}, use === 'on-dark' && {
      color: 'text-primary-on-fill-default'
    }, {
      children: renderedLabel
    }))]
  }));
};